import { DEFAULT_LOCALE, ENABLED_LOCALES, localizedPath } from '@/shared/config/locales';
import type { AppLocale } from '@/shared/lib';

import { absoluteUrl, BRAND, OG_LOCALE } from './site';

export type JsonLd = Record<string, unknown>;

/** Everything a page contributes to <head>, as data. */
export type HeadTags = {
  title: string;
  meta: { name?: string; property?: string; content: string }[];
  links: { rel: string; href: string; hrefLang?: string }[];
  jsonLd: JsonLd[];
};

export type SeoInput = {
  title: string;
  description: string;
  locale: AppLocale;
  /** App path without the locale prefix, e.g. "/pricing". */
  path: string;
  noindex?: boolean;
  image?: string;
  type?: 'website' | 'profile';
  jsonLd?: JsonLd[];
  /** The locales to advertise; the build's enabled set unless a test says otherwise. */
  locales?: readonly AppLocale[];
};

/**
 * Builds the head tags for one page.
 *
 * Indexable pages get a self-referencing canonical plus hreflang alternates
 * for every *enabled* locale and an x-default pointing at the canonical
 * locale. A switched-off locale is never advertised: its URLs redirect, and an
 * alternate that redirects is one search engines drop with a warning.
 * noindex pages get only a title and the robots directive: advertising
 * alternates for a page that must not be indexed would contradict it.
 */
export const buildHeadTags = ({
  title,
  description,
  locale,
  path,
  noindex = false,
  image,
  type = 'website',
  jsonLd = [],
  locales = ENABLED_LOCALES,
}: SeoInput): HeadTags => {
  if (noindex) {
    return {
      title,
      meta: [{ name: 'robots', content: 'noindex, nofollow' }],
      links: [],
      jsonLd: [],
    };
  }

  const url = absoluteUrl(localizedPath(locale, path));
  const meta: HeadTags['meta'] = [
    { name: 'description', content: description },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:type', content: type },
    { property: 'og:site_name', content: BRAND },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: OG_LOCALE[locale] },
    ...locales
      .filter((item) => item !== locale)
      .map((item) => ({
        property: 'og:locale:alternate',
        content: OG_LOCALE[item],
      })),
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    ...(image
      ? [
          { property: 'og:image', content: image },
          { name: 'twitter:image', content: image },
        ]
      : []),
  ];
  const links: HeadTags['links'] = [
    { rel: 'canonical', href: url },
    ...locales.map((item) => ({
      rel: 'alternate',
      hrefLang: item,
      href: absoluteUrl(localizedPath(item, path)),
    })),
    {
      rel: 'alternate',
      hrefLang: 'x-default',
      href: absoluteUrl(localizedPath(DEFAULT_LOCALE, path)),
    },
  ];
  return { title, meta, links, jsonLd };
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** JSON for an inline script: "<" is escaped so "</script>" in data cannot close it. */
export const serializeJson = (value: unknown): string =>
  JSON.stringify(value).replace(/</g, '\\u003c');

const attrs = (record: Record<string, string | undefined>) =>
  Object.entries(record)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => ` ${key}="${escapeHtml(value!)}"`)
    .join('');

/** The tags as HTML, for the prerendered document. */
export const renderHeadTags = (tags: HeadTags): string =>
  [
    `<title data-seo>${escapeHtml(tags.title)}</title>`,
    ...tags.meta.map(
      ({ name, property, content }) => `<meta data-seo${attrs({ name, property, content })}>`,
    ),
    ...tags.links.map(
      ({ rel, href, hrefLang }) => `<link data-seo${attrs({ rel, hreflang: hrefLang, href })}>`,
    ),
    ...tags.jsonLd.map(
      (data) => `<script data-seo type="application/ld+json">${serializeJson(data)}</script>`,
    ),
  ].join('\n    ');

/** Replaces the previous page's tags in the live document. */
export const applyHeadTags = (tags: HeadTags, doc: Document = document): void => {
  // Remove first: the prerendered <title data-seo> is one of these nodes, and
  // setting document.title before removing it would leave the page untitled.
  doc.head.querySelectorAll('[data-seo]').forEach((node) => node.remove());
  doc.title = tags.title;
  const add = (tag: string, record: Record<string, string | undefined>, text?: string) => {
    const node = doc.createElement(tag);
    node.setAttribute('data-seo', '');
    Object.entries(record).forEach(
      ([key, value]) => value !== undefined && node.setAttribute(key, value),
    );
    if (text !== undefined) node.textContent = text;
    doc.head.appendChild(node);
  };
  tags.meta.forEach(({ name, property, content }) => add('meta', { name, property, content }));
  tags.links.forEach(({ rel, href, hrefLang }) => add('link', { rel, hreflang: hrefLang, href }));
  tags.jsonLd.forEach((data) =>
    add('script', { type: 'application/ld+json' }, JSON.stringify(data)),
  );
};
